<template>
  <div class="conversion-create-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Top Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 500;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header -->
    <div class="page-header-row mb-4">
      <div class="header-left">
        <PageBreadcrumb :items="[{ text: 'Conversion', to: '/ConversionList' }, { text: 'New Metal Conversion' }]" class="mb-1" />
        <p class="page-desc">Convert stock scrap, old inventory, or raw gold into newly catalogued jewellery designs.</p>
      </div>

      <div class="header-right">
        <button class="secondary-back-btn" @click="$router.push('/ConversionList')">
          <v-icon size="16" color="#64748b" class="mr-1">mdi-arrow-left</v-icon>
          <span>Back to List</span>
        </button>
      </div>
    </div>

    <!-- Main Form Card -->
    <div class="form-main-card">
      <!-- Section 1: Voucher Details -->
      <div class="card-section">
        <div class="section-title-row mb-3">
          <v-icon size="18" color="#9a6b27" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="section-title">Voucher & Date Details</span>
        </div>

        <v-layout wrap>
          <!-- Entry No -->
          <v-flex xs12 sm4 md3 pr-sm-3 pb-3>
            <label class="input-field-label">Entry Number</label>
            <v-text-field
              v-model="entryNo"
              disabled
              outlined
              dense
              hide-details
              class="luxury-text-field"
            ></v-text-field>
          </v-flex>

          <!-- Date -->
          <v-flex xs12 sm4 md3 pr-sm-3 pb-3>
            <label class="input-field-label">Conversion Date</label>
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  readonly
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                  class="luxury-text-field"
                ></v-text-field>
              </template>
              <v-date-picker v-model="date" no-title scrollable color="#9a6b27" @change="$refs.dateMenu.save(date)">
                <v-spacer></v-spacer>
              </v-date-picker>
            </v-menu>
          </v-flex>

          <!-- Remarks -->
          <v-flex xs12 sm4 md6 pb-3>
            <label class="input-field-label">Remarks / Conversion Notes</label>
            <v-text-field
              v-model="remarks"
              placeholder="e.g. Scrap bangles melted into 22K round necklace"
              outlined
              dense
              hide-details
              class="luxury-text-field"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </div>

      <!-- Section 2: Conversion Flow (FROM & TO Cards) -->
      <div class="conversion-split-grid mt-4">
        <!-- FROM SECTION -->
        <div class="panel-box from-box">
          <div class="panel-header from-header">
            <div class="d-flex align-center">
              <span class="panel-tag from-tag">SOURCE</span>
              <span class="panel-heading">FROM (Stock to Melt / Re-make)</span>
            </div>
            <v-icon size="20" color="#b45309">mdi-source-branch</v-icon>
          </div>

          <div class="panel-body pa-4">
            <div class="mb-3">
              <label class="input-field-label">Select Stock Item to Convert <span class="req">*</span></label>
              <v-autocomplete
                v-model="fromPurchaseItemId"
                :items="fromItemOptions"
                item-text="label"
                item-value="_id"
                placeholder="Search by design, code or HUID..."
                outlined
                dense
                hide-details
                class="luxury-text-field"
                @change="onFromItemSelect"
              ></v-autocomplete>
            </div>

            <template v-if="source">
              <div class="source-facts mb-3">
                <div class="fact"><span>Metal</span><b>{{ source.metalType || 'Gold' }}</b></div>
                <div class="fact"><span>In stock</span><b>{{ source.pieceStock ? source.count + ' pcs · ' : '' }}{{ fmt3(source.netWeight) }} g</b></div>
                <div class="fact"><span>Cost / g</span><b>₹ {{ fmt2(source.costPerGram) }}</b></div>
                <div class="fact" v-if="source.supplierId"><span>Supplier</span><b>{{ source.supplierId.supplierName || source.supplierId.name || '-' }}</b></div>
              </div>

              <div class="huid-warning mb-3" v-if="source.HUID">
                <v-icon size="16" color="#b45309" class="mr-1">mdi-alert-outline</v-icon>
                Hallmarked piece (HUID {{ source.HUID }}). Melting it voids this hallmark — the new items need fresh hallmarking.
              </div>

              <v-layout wrap>
                <v-flex xs12 sm6 pr-sm-2 pb-3 v-if="source.pieceStock">
                  <label class="input-field-label">Pieces to Convert <span class="req">*</span></label>
                  <v-text-field
                    v-model="consumeCount"
                    type="number"
                    min="1"
                    :max="source.count"
                    outlined
                    dense
                    hide-details
                    class="luxury-text-field"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 pr-sm-2 pb-3 v-else>
                  <label class="input-field-label">Net Weight to Convert (g) <span class="req">*</span></label>
                  <v-text-field
                    v-model="consumeNetWeight"
                    type="number"
                    step="0.001"
                    outlined
                    dense
                    hide-details
                    class="luxury-text-field"
                  >
                    <template v-slot:append>
                      <a class="use-all-link" @click="consumeNetWeight = fmt3(source.netWeight)">All</a>
                    </template>
                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <div class="calc-weight-banner from-banner">
                    <span class="calc-label">Metal Consumed (net):</span>
                    <span class="calc-val">{{ fmt3(consumedNet) }} g</span>
                  </div>
                  <div class="remaining-note mt-1">
                    Remaining in stock after conversion:
                    {{ source.pieceStock ? Math.max(0, source.count - (parseFloat(consumeCount) || 0)) + ' pcs · ' : '' }}{{ fmt3(Math.max(0, source.netWeight - consumedNet)) }} g
                  </div>
                </v-flex>
              </v-layout>
            </template>
            <div v-else class="empty-hint">Pick the stock item you are melting or re-making.</div>
          </div>
        </div>

        <!-- TO SECTION -->
        <div class="panel-box to-box">
          <div class="panel-header to-header">
            <div class="d-flex align-center">
              <span class="panel-tag to-tag">TARGET</span>
              <span class="panel-heading">TO (Resulting Ornaments)</span>
            </div>
            <v-icon size="20" color="#16a34a">mdi-creation</v-icon>
          </div>

          <div class="panel-body pa-4">
            <div v-for="(row, ri) in toRows" :key="row.key" class="output-row mb-3">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="output-row-title">Output {{ ri + 1 }}</span>
                <v-btn v-if="toRows.length > 1" icon x-small @click="toRows.splice(ri, 1)" title="Remove">
                  <v-icon size="16" color="#b91c1c">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
              <v-layout wrap>
                <v-flex xs12 pb-3>
                  <label class="input-field-label">Design <span class="req">*</span></label>
                  <v-autocomplete
                    v-model="row.designId"
                    :items="targetDesignOptions"
                    item-text="label"
                    item-value="_id"
                    placeholder="Search catalog design..."
                    outlined
                    dense
                    hide-details
                    class="luxury-text-field"
                    @change="onToDesignSelect(row)"
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm4 pr-2 pb-3>
                  <label class="input-field-label">Stock As</label>
                  <v-select
                    v-model="row.countOrSplit"
                    :items="['Count', 'Split']"
                    outlined
                    dense
                    hide-details
                    class="luxury-text-field"
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm4 pr-sm-2 pb-3 v-if="row.countOrSplit === 'Count'">
                  <label class="input-field-label">Pieces</label>
                  <v-text-field v-model="row.count" type="number" min="1" outlined dense hide-details class="luxury-text-field"></v-text-field>
                </v-flex>
                <v-flex xs6 sm4 pb-3 v-if="(source && source.metalType) === 'Diamond'">
                  <label class="input-field-label">Cent Weight (ct)</label>
                  <v-text-field v-model="row.centWeight" type="number" step="0.01" outlined dense hide-details class="luxury-text-field"></v-text-field>
                </v-flex>
                <v-flex xs6 pr-2 pb-2>
                  <label class="input-field-label">Gross Weight (g)</label>
                  <v-text-field v-model="row.grossWeight" type="number" step="0.001" outlined dense hide-details class="luxury-text-field"></v-text-field>
                </v-flex>
                <v-flex xs6 pb-2>
                  <label class="input-field-label">Stone Weight (g)</label>
                  <v-text-field v-model="row.stoneWeight" type="number" step="0.001" outlined dense hide-details class="luxury-text-field"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <div class="row-net">Net: <b>{{ fmt3(rowNet(row)) }} g</b></div>
                </v-flex>
              </v-layout>
            </div>

            <div class="d-flex align-center justify-space-between flex-wrap">
              <button class="add-output-btn" @click="addToRow()">
                <v-icon size="16" color="#9a6b27" class="mr-1">mdi-plus</v-icon>
                Add another output item
              </button>
              <v-checkbox
                v-model="showAllCategories"
                label="Show designs from all categories"
                dense
                hide-details
                color="#9a6b27"
                class="mt-0 pt-0 all-cat-check"
              ></v-checkbox>
            </div>

            <div class="calc-weight-banner to-banner mt-3">
              <span class="calc-label">Total Output (net):</span>
              <span class="calc-val">{{ fmt3(outputNet) }} g</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 3: Loss & Cost Summary -->
      <div class="loss-summary mt-4" v-if="source">
        <div class="loss-cell">
          <span class="loss-label">Consumed</span>
          <span class="loss-val">{{ fmt3(consumedNet) }} g</span>
        </div>
        <div class="loss-cell">
          <span class="loss-label">Output</span>
          <span class="loss-val">{{ fmt3(outputNet) }} g</span>
        </div>
        <div class="loss-cell" :class="lossClass">
          <span class="loss-label">{{ lossTitle }}</span>
          <span class="loss-val">{{ fmt3(Math.abs(lossWeight)) }} g · {{ fmt2(Math.abs(lossPercent)) }}%</span>
        </div>
        <div class="loss-cell">
          <span class="loss-label">Loss Value</span>
          <span class="loss-val">₹ {{ fmt2(Math.max(0, lossWeight) * (source.costPerGram || 0)) }}</span>
        </div>
        <div class="loss-cell">
          <span class="loss-label">New Cost / g</span>
          <span class="loss-val">₹ {{ fmt2(outputCostPerGram) }}</span>
        </div>
        <div class="loss-message" v-if="isOverWeight">
          Output is heavier than the metal consumed — reduce the output weight or convert more.
        </div>
        <div class="loss-message warn" v-else-if="lossPercent > lossWarnPercent">
          Loss is above {{ lossWarnPercent }}% — please double-check the weights before saving.
        </div>
        <div class="loss-note" v-else>
          The loss is absorbed into the new items' cost per gram, so profit on sale stays correct.
        </div>
      </div>

      <!-- Action Footer -->
      <div class="form-action-footer mt-5 pt-4">
        <button class="secondary-btn mr-3" @click="$router.push('/ConversionList')">
          Cancel
        </button>
        <button class="luxury-save-btn" @click="save">
          <v-icon size="18" color="#ffffff" class="mr-1">mdi-check-circle-outline</v-icon>
          <span>Save Conversion Voucher</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

var rowKey = 0;
function newRow(design) {
  rowKey += 1;
  return {
    key: rowKey,
    designId: design ? design._id : null,
    countOrSplit: design && design.raw.countOrSplit === "Split" ? "Split" : "Count",
    count: 1,
    grossWeight: "",
    stoneWeight: "",
    centWeight: "",
  };
}

export default {
  name: "Conversion",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      dateMenu: false,
      prefix: "",
      entryNo: "",
      checkEntryNo: null,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      remarks: "",
      fromItemOptions: [],
      fromPurchaseItemId: null,
      consumeCount: 1,
      consumeNetWeight: "",
      designOptions: [],
      showAllCategories: false,
      toRows: [newRow()],
      // above this the voucher is flagged for a second look (not blocked)
      lossWarnPercent: 5,
    };
  },
  computed: {
    source() {
      var option = this.fromItemOptions.find((o) => o._id === this.fromPurchaseItemId);
      return option ? option.raw : null;
    },
    sourceCategoryId() {
      var d = this.source && this.source.designId;
      if (!d || !d.categoryId) return null;
      return d.categoryId._id || d.categoryId;
    },
    // same category (purity) first; everything when asked or when none match
    targetDesignOptions() {
      if (this.showAllCategories || !this.sourceCategoryId) return this.designOptions;
      var same = this.designOptions.filter((d) => String(d.raw.categoryId && (d.raw.categoryId._id || d.raw.categoryId)) === String(this.sourceCategoryId));
      return same.length ? same : this.designOptions;
    },
    consumedNet() {
      var s = this.source;
      if (!s) return 0;
      if (s.pieceStock) {
        var c = parseFloat(this.consumeCount) || 0;
        if (c >= s.count) return s.netWeight;
        return (s.netWeight / s.count) * c;
      }
      return parseFloat(this.consumeNetWeight) || 0;
    },
    outputNet() {
      return this.toRows.reduce((sum, r) => sum + this.rowNet(r), 0);
    },
    lossWeight() {
      return this.consumedNet - this.outputNet;
    },
    lossPercent() {
      return this.consumedNet > 0 ? (this.lossWeight / this.consumedNet) * 100 : 0;
    },
    outputCostPerGram() {
      if (!this.source || !(this.outputNet > 0)) return 0;
      return (this.consumedNet * (this.source.costPerGram || 0)) / this.outputNet;
    },
    isOverWeight() {
      return this.lossWeight < -0.0005;
    },
    lossTitle() {
      return this.isOverWeight ? "Over by" : "Melting / Making Loss";
    },
    lossClass() {
      if (this.isOverWeight) return "is-error";
      if (this.lossPercent > this.lossWarnPercent) return "is-warn";
      return "is-ok";
    },
  },
  mounted() {
    this.generateEntryNo();
    this.getFromItemOptions();
    this.getDesignOptions();
  },
  methods: {
    fmt2(n) {
      return (parseFloat(n) || 0).toFixed(2);
    },
    fmt3(n) {
      return (parseFloat(n) || 0).toFixed(3);
    },
    rowNet(row) {
      return Math.max(0, (parseFloat(row.grossWeight) || 0) - (parseFloat(row.stoneWeight) || 0));
    },
    addToRow() {
      this.toRows.push(newRow());
    },
    generateEntryNo() {
      axios({
        method: "get",
        url: "/conversion/bill/generate",
        headers: { "x-auth-token": localStorage.getItem("token") },
      }).then((response) => {
        if (response.data.status) {
          this.entryNo = response.data.entryNo;
          this.checkEntryNo = response.data.checkEntryNo;
          this.prefix = response.data.prefix;
        }
      });
    },
    getFromItemOptions() {
      axios({
        method: "get",
        url: "/conversion/fromitem/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      }).then((response) => {
        if (response.data.status) {
          this.fromItemOptions = response.data.data.map((it) => {
            var d = it.designId || {};
            var name = d.name ? d.name + (d.code ? " (" + d.code + ")" : "") : d.code || "Item";
            var qty = (it.pieceStock ? it.count + " pcs, " : "") + (it.netWeight || 0).toFixed(3) + " g";
            var tags = [it.isBulk ? "Bulk" : null, it.conversionId ? "Converted" : null, it.HUID ? "HUID " + it.HUID : null].filter(Boolean);
            return {
              _id: it._id,
              label: name + " - " + qty + (it.supplierDesignCode ? " · " + it.supplierDesignCode : "") + (tags.length ? " · " + tags.join(" · ") : ""),
              raw: it,
            };
          });
        }
      });
    },
    getDesignOptions() {
      axios({
        method: "get",
        url: "/design/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { count: 0, page: 0 },
      }).then((response) => {
        if (response.data.status) {
          this.designOptions = response.data.data.map((d) => ({
            _id: d._id,
            label: (d.code ? d.code + " - " : "") + d.name,
            raw: d,
          }));
        }
      });
    },
    onFromItemSelect() {
      this.consumeCount = 1;
      this.consumeNetWeight = "";
    },
    onToDesignSelect(row) {
      var option = this.designOptions.find((o) => o._id === row.designId);
      if (option) {
        row.countOrSplit = option.raw.countOrSplit === "Split" ? "Split" : "Count";
      }
    },
    validate() {
      var s = this.source;
      if (!s) return "Please select the item to convert from";
      if (s.pieceStock) {
        var c = parseFloat(this.consumeCount);
        if (!(c >= 1) || c % 1 !== 0 || c > s.count) return "Pieces to convert must be a whole number from 1 to " + s.count;
      } else {
        var w = parseFloat(this.consumeNetWeight);
        if (!(w > 0) || w > s.netWeight + 0.0005) return "Weight to convert must be more than 0 and at most " + this.fmt3(s.netWeight) + " g";
      }
      for (var i = 0; i < this.toRows.length; i++) {
        var r = this.toRows[i];
        var label = this.toRows.length > 1 ? " (output " + (i + 1) + ")" : "";
        if (!r.designId) return "Please select the design" + label;
        if (r.countOrSplit === "Count" && (!(parseFloat(r.count) >= 1) || parseFloat(r.count) % 1 !== 0)) return "Enter the number of pieces" + label;
        if (!(this.rowNet(r) > 0)) return "Net weight must be more than 0" + label;
      }
      if (this.lossWeight < -0.0005) return "Output weight is more than the metal consumed";
      return null;
    },
    save() {
      var err = this.validate();
      if (err) {
        this.msg = err;
        this.showsnackbar = true;
        return;
      }
      if (this.lossPercent > this.lossWarnPercent &&
          !window.confirm("Loss is " + this.fmt2(this.lossPercent) + "% (" + this.fmt3(this.lossWeight) + " g). Save anyway?")) {
        return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/conversion/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          prefix: this.prefix,
          entryNo: this.entryNo,
          checkEntryNo: this.checkEntryNo,
          date: this.date,
          remarks: this.remarks,
          fromItem: {
            purchaseItemId: this.fromPurchaseItemId,
            count: this.source.pieceStock ? parseFloat(this.consumeCount) : 0,
            netWeight: this.source.pieceStock ? 0 : parseFloat(this.consumeNetWeight),
          },
          toItems: this.toRows.map((r) => ({
            designId: r.designId,
            countOrSplit: r.countOrSplit,
            count: r.countOrSplit === "Count" ? parseFloat(r.count) : 0,
            grossWeight: parseFloat(r.grossWeight) || 0,
            stoneWeight: parseFloat(r.stoneWeight) || 0,
            centWeight: parseFloat(r.centWeight) || 0,
          })),
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status) {
            this.$router.push("/ConversionList");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.conversion-create-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 18px 24px 40px 24px;
  background-color: transparent;
  color: #172238;
}

.page-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.eyebrow-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9a6b27;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.diamond-bullet {
  font-size: 13px;
  margin-right: 6px;
  color: #9a6b27;
}

.page-title {
  font-family: 'Cinzel', 'Inter', serif;
  font-size: 26px;
  font-weight: 700;
  color: #172238;
  letter-spacing: -0.2px;
  line-height: 1.2;
  margin: 0;
}

.page-desc {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.secondary-back-btn {
  background: #ffffff;
  border: 1px solid #e2dcce;
  border-radius: 9px;
  padding: 8px 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s;
}

.secondary-back-btn:hover {
  background: #fbfaf8;
  border-color: #b9893f;
  color: #9a6b27;
}

.form-main-card {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.card-section {
  background: #fcfbf9;
  border: 1px solid #ebe5db;
  border-radius: 12px;
  padding: 16px 20px;
}

.section-title-row {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #172238;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.input-field-label {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 5px;
}

.req {
  color: #dc2626;
}

.luxury-text-field >>> .v-input__slot {
  background: #ffffff !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  border-color: #e2dcce !important;
}

.conversion-split-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 900px) {
  .conversion-split-grid {
    grid-template-columns: 1fr;
  }
}

.panel-box {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 14px;
  overflow: hidden;
}

.from-box {
  border-top: 3px solid #b45309;
}

.to-box {
  border-top: 3px solid #16a34a;
}

.panel-header {
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f0ebe4;
}

.from-header {
  background: #fefcf9;
}

.to-header {
  background: #f9fdfa;
}

.panel-tag {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 8px;
}

.from-tag {
  background: #fffbeb;
  color: #b45309;
}

.to-tag {
  background: #ecfdf5;
  color: #059669;
}

.panel-heading {
  font-size: 13px;
  font-weight: 700;
  color: #172238;
}

.calc-weight-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 8px;
  margin-top: 4px;
}

.from-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.to-banner {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.calc-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.calc-val {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #172238;
}

.source-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}
.source-facts .fact {
  background: #fffaf2;
  border: 1px solid #f1e3c8;
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #64748b;
}
.source-facts .fact b {
  color: #1e293b;
  font-size: 13px;
}
.huid-warning {
  font-size: 12px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 6px 10px;
}
.remaining-note,
.empty-hint,
.row-net {
  font-size: 12px;
  color: #64748b;
}
.use-all-link {
  font-size: 12px;
  font-weight: 600;
  color: #9a6b27;
  cursor: pointer;
  align-self: center;
}
.output-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  background: #ffffff;
}
.output-row-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #16a34a;
  text-transform: uppercase;
}
.add-output-btn {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #9a6b27;
  padding: 6px 10px;
  border: 1px dashed #d6b98a;
  border-radius: 8px;
  background: #fffaf2;
}
.all-cat-check >>> label {
  font-size: 12px;
}
.loss-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  border: 1px solid #eee2cc;
  background: #faf7f0;
  border-radius: 12px;
  padding: 14px;
}
.loss-cell {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #efe6d4;
  border-radius: 8px;
  padding: 8px 12px;
}
.loss-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.loss-val {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.loss-cell.is-ok .loss-val {
  color: #15803d;
}
.loss-cell.is-warn .loss-val {
  color: #b45309;
}
.loss-cell.is-error .loss-val {
  color: #b91c1c;
}
.loss-message,
.loss-note {
  grid-column: 1 / -1;
  font-size: 12px;
}
.loss-message {
  color: #b91c1c;
  font-weight: 600;
}
.loss-message.warn {
  color: #b45309;
}
.loss-note {
  color: #64748b;
}
.form-action-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #f0ebe4;
}

.secondary-btn {
  background: #fbfaf8;
  border: 1px solid #e2dcce;
  border-radius: 10px;
  padding: 11px 22px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.secondary-btn:hover {
  background: #ffffff;
  color: #172238;
}

.luxury-save-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  border: none;
  border-radius: 10px;
  padding: 11px 24px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28);
  transition: all 0.25s ease;
}

.luxury-save-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38);
  transform: translateY(-1.5px);
}
</style>
